// Recebíveis em regime de caixa: o dinheiro conta no mês em que ENTROU, não no mês da venda.
//
// Antes, "recebido" de um período era o received_value das vendas FEITAS no período, então uma parcela
// paga em outubro de uma venda de setembro aparecia como recebimento de setembro. Aqui cada entrada de
// dinheiro vira um evento com a sua própria data:
//   - entrada/sinal: o que a venda recebeu além das parcelas pagas, na data da venda;
//   - parcela paga: na data em que foi paga (paid_date).
// "A receber" é o contrário: o saldo ainda em aberto, agrupado pelo mês de VENCIMENTO.
//
// Arquivo sem import de Deno, para ser testado com vitest e usado tanto pelo front quanto pela edge
// function payroll-automation (a comissão da folha depende do líquido recebido no mês).

export const SEM_FORMA = 'Não informada';

const semAcento = (t: string) => t.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/** Nome canônico da forma de pagamento: junta variações de maiúscula, acento e parcelamento ("pix" e "Pix", "Cartão em 3x") */
export const formaPagamento = (m?: string | null): string => {
    const bruto = (m || '').trim().replace(/\s+/g, ' ');
    if (!bruto) return SEM_FORMA;
    const k = semAcento(bruto).toLowerCase();
    if (k === 'nao informada' || k === 'nao informado') return SEM_FORMA;
    if (k.includes('pix')) return 'Pix';
    if (k.includes('cartao') || k.includes('credito') || k.includes('debito')) {
        return k.includes('debito') ? 'Cartão de Débito' : 'Cartão de Crédito';
    }
    if (k.includes('boleto')) return 'Boleto';
    if (k.includes('dinheiro') || k.includes('especie')) return 'Dinheiro';
    if (k.includes('transfer') || k === 'ted' || k === 'doc') return 'Transferência';
    return bruto.charAt(0).toUpperCase() + bruto.slice(1);
};

export interface PercentuaisForma {
    taxa: number;
    imposto: number;
}

export type ConfigFormas = Record<string, PercentuaisForma>;

export const normalizarConfigFormas = (config?: ConfigFormas | null): ConfigFormas => {
    const out: ConfigFormas = {};
    for (const [chave, v] of Object.entries(config || {})) out[formaPagamento(chave)] = v;
    return out;
};

export interface ParcelaRec {
    /** id em sale_installments (quando a tela precisa dar baixa/desfazer naquela parcela) */
    id?: number | null;
    value?: number | null;
    status?: string | null;
    due_date?: string | null;
    paid_date?: string | null;
    payment_method?: string | null;
}

export interface VendaRec {
    id: number;
    user_id?: string | null;
    sale_date: string;
    value?: number | null;
    total_value?: number | null;
    received_value?: number | null;
    down_payment?: number | null;
    payment_status?: string | null;
    payment_method?: string | null;
    next_due_date?: string | null;
    status?: string | null;
    sale_installments?: ParcelaRec[] | null;
}

export interface EventoRecebimento {
    saleId: number;
    userId: string | null;
    /** AAAA-MM-DD (dia local, America/Sao_Paulo) */
    data: string;
    valor: number;
    forma: string;
    origem: 'entrada' | 'parcela';
    /** Parcela de onde veio o dinheiro (origem 'parcela') */
    parcelaId?: number | null;
}

export interface ItemAReceber {
    saleId: number;
    userId: string | null;
    /** AAAA-MM-DD */
    vencimento: string;
    valor: number;
    /** Saldo da venda que não tem parcela cadastrada (vence na data prevista da venda). */
    semParcela: boolean;
    /** Parcela em aberto que este item representa por inteiro (dá para dar baixa nela) */
    parcelaId?: number | null;
}

export interface ResumoAReceber {
    total: number;
    vencido: number;
    /** Mês de vencimento (AAAA-MM) → valor */
    porMes: Record<string, number>;
    /** Parte do total que não tem parcela cadastrada */
    semParcela: number;
}

const FUSO = 'America/Sao_Paulo';
const num = (n: unknown) => Number(n) || 0;
const arred = (n: number) => Math.round(n * 100) / 100;
const CENTAVO = 0.005;

/** Data local (AAAA-MM-DD) de um timestamp ou data. Datas puras (AAAA-MM-DD) passam direto. */
export function diaLocal(valor?: string | null, fuso: string = FUSO): string {
    const v = (valor || '').trim();
    if (!v) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return v.slice(0, 10);
    // en-CA formata como AAAA-MM-DD
    return new Intl.DateTimeFormat('en-CA', { timeZone: fuso, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

export const mesDe = (data: string): string => data.slice(0, 7);

/** Hoje (AAAA-MM-DD) no fuso do negócio */
export const hojeLocal = (agora: Date = new Date()): string => diaLocal(agora.toISOString());

export const totalDaVenda = (v: VendaRec): number => num(v.total_value) || num(v.value);

const parcelasPagas = (v: VendaRec) => (v.sale_installments || []).filter(p => p.status === 'paid');
const somaParcelas = (ps: ParcelaRec[]) => ps.reduce((s, p) => s + num(p.value), 0);

/**
 * Quanto a venda já recebeu. O valor gravado na venda (received_value/down_payment) é o acumulado:
 * já inclui as parcelas pagas por "Somar pagamento". Se as parcelas pagas somarem mais do que o gravado
 * (baixa direta na parcela), vale o das parcelas.
 */
export function recebidoDaVenda(v: VendaRec): number {
    const pagas = somaParcelas(parcelasPagas(v));
    let recebido = Math.max(num(v.received_value), num(v.down_payment), pagas);
    // Venda antiga marcada como paga sem nenhum valor recebido gravado: considera quitada.
    if (v.received_value == null && v.down_payment == null && pagas === 0 && v.payment_status === 'paid') {
        recebido = totalDaVenda(v);
    }
    return recebido;
}

export const saldoDaVenda = (v: VendaRec): number => Math.max(arred(totalDaVenda(v) - recebidoDaVenda(v)), 0);

/** Soma das parcelas já pagas da venda (o piso do "recebido": abaixo disso o valor não pode ser reduzido sem desfazer parcelas). */
export const pagoEmParcelas = (v: VendaRec): number => arred(somaParcelas(parcelasPagas(v)));

/**
 * Total, recebido e saldo de uma venda pela MESMA regra em qualquer tela. Telas não devem mais fazer
 * `received_value || down_payment` nem `total - recebido` por conta própria: foi isso que fez a Vendas, o
 * Financeiro e o painel do CEO mostrarem valores diferentes para a mesma venda.
 */
export function resumoDaVenda(v: VendaRec): { total: number; recebido: number; saldo: number } {
    const total = totalDaVenda(v);
    const recebido = arred(recebidoDaVenda(v));
    return { total, recebido, saldo: Math.max(arred(total - recebido), 0) };
}

/** Cada entrada de dinheiro da venda, com a data em que ela de fato aconteceu. */
export function eventosDaVenda(v: VendaRec): EventoRecebimento[] {
    if (v.status === 'cancelled') return [];
    const eventos: EventoRecebimento[] = [];
    const pagas = parcelasPagas(v);
    const entrada = recebidoDaVenda(v) - somaParcelas(pagas);
    const dataVenda = diaLocal(v.sale_date);
    const formaVenda = formaPagamento(v.payment_method);
    if (entrada > CENTAVO) {
        eventos.push({ saleId: v.id, userId: v.user_id ?? null, data: dataVenda, valor: arred(entrada), forma: formaVenda, origem: 'entrada' });
    }
    for (const p of pagas) {
        const valor = num(p.value);
        if (valor <= 0) continue;
        eventos.push({
            saleId: v.id,
            userId: v.user_id ?? null,
            data: diaLocal(p.paid_date) || diaLocal(p.due_date) || dataVenda,
            valor: arred(valor),
            forma: formaPagamento(p.payment_method || v.payment_method),
            origem: 'parcela',
            parcelaId: p.id ?? null,
        });
    }
    return eventos;
}

export const eventosDeVendas = (vendas: VendaRec[]): EventoRecebimento[] => vendas.flatMap(eventosDaVenda);

/** Eventos cuja data cai no mês (AAAA-MM). */
export const eventosDoMes = (eventos: EventoRecebimento[], mes: string): EventoRecebimento[] =>
    eventos.filter(e => mesDe(e.data) === mes);

export const somarEventos = (eventos: EventoRecebimento[]): number => arred(eventos.reduce((s, e) => s + e.valor, 0));

/** Recebido bruto no mês (AAAA-MM). */
export const recebidoNoMes = (eventos: EventoRecebimento[], mes: string): number => somarEventos(eventosDoMes(eventos, mes));

/** Parte do valor que sobra depois de taxas e impostos da forma de pagamento em que entrou. */
export function liquidoDoEvento(e: EventoRecebimento, config: ConfigFormas): number {
    const c = config[e.forma] || { taxa: 0, imposto: 0 };
    return e.valor * (1 - ((c.taxa || 0) + (c.imposto || 0)) / 100);
}

/** Recebido líquido (depois de taxas e impostos) dos eventos informados. */
export const liquidoDosEventos = (eventos: EventoRecebimento[], config?: ConfigFormas | null): number => {
    const cfg = normalizarConfigFormas(config);
    return arred(eventos.reduce((s, e) => s + liquidoDoEvento(e, cfg), 0));
};

/**
 * O que ainda falta receber de uma venda, por data de vencimento. As parcelas em aberto entram com o seu
 * vencimento, até o limite do saldo da venda; o que sobra do saldo sem parcela vence na data prevista da venda.
 */
export function aReceberDaVenda(v: VendaRec): ItemAReceber[] {
    if (v.status === 'cancelled') return [];
    let saldo = saldoDaVenda(v);
    if (saldo <= CENTAVO) return [];
    const dataPrevista = diaLocal(v.next_due_date) || diaLocal(v.sale_date);
    const itens: ItemAReceber[] = [];

    const abertas = (v.sale_installments || [])
        .filter(p => p.status !== 'paid' && num(p.value) > 0)
        .map(p => ({ valor: num(p.value), vencimento: diaLocal(p.due_date) || dataPrevista, id: p.id ?? null }))
        .sort((a, b) => a.vencimento.localeCompare(b.vencimento));

    for (const p of abertas) {
        if (saldo <= CENTAVO) break;
        const valor = Math.min(p.valor, saldo);
        // Só dá para dar baixa na parcela quando o item é ela inteira (não foi limitada pelo saldo)
        itens.push({ saleId: v.id, userId: v.user_id ?? null, vencimento: p.vencimento, valor: arred(valor), semParcela: false, parcelaId: valor === p.valor ? p.id : null });
        saldo -= valor;
    }
    if (saldo > CENTAVO) {
        itens.push({ saleId: v.id, userId: v.user_id ?? null, vencimento: dataPrevista, valor: arred(saldo), semParcela: true });
    }
    return itens;
}

export const aReceberDeVendas = (vendas: VendaRec[]): ItemAReceber[] => vendas.flatMap(aReceberDaVenda);

export function resumirAReceber(itens: ItemAReceber[], hoje: string = hojeLocal()): ResumoAReceber {
    const resumo: ResumoAReceber = { total: 0, vencido: 0, porMes: {}, semParcela: 0 };
    for (const i of itens) {
        resumo.total += i.valor;
        if (i.vencimento < hoje) resumo.vencido += i.valor;
        if (i.semParcela) resumo.semParcela += i.valor;
        const mes = mesDe(i.vencimento);
        resumo.porMes[mes] = arred((resumo.porMes[mes] || 0) + i.valor);
    }
    resumo.total = arred(resumo.total);
    resumo.vencido = arred(resumo.vencido);
    resumo.semParcela = arred(resumo.semParcela);
    return resumo;
}

/** Soma de valores por usuário (vendedor) */
export function somarPorUsuario<T extends { userId: string | null; valor: number }>(itens: T[]): Map<string, number> {
    const mapa = new Map<string, number>();
    for (const i of itens) {
        const k = i.userId || 'sem-vendedor';
        mapa.set(k, arred((mapa.get(k) || 0) + i.valor));
    }
    return mapa;
}
